// ============================================================================
// EVIDENCE CONCEPT BUILDER — the pure kernel of the concept library
// (Phase L-1 — behavior-preserving module split)
//
// This module holds the SCHEMA and the MACHINERY (enums, defaults, the
// makeConcept factory, the per-concept validator, target resolution, and the
// pure concept→authoring-shape projections). It holds NO concept data — the
// concept records live in the per-specialty data modules (shared.js,
// neurology.js, hematology.js) and are aggregated by index.js.
//
//   conceptBuilder.js  → schema + makeConcept + validators + projections (here)
//   shared/<specialty>.js → concept records + their recognition patterns
//   index.js           → aggregate + ordered recognition tables + read helpers
//   evidenceConceptLibrary.js → facade (identical public API)
//
// EXACTLY the same code as the former inline kernel of evidenceConceptLibrary.js
// (Phase 2C-2a). Nothing here changes behavior. `makeConcept` is exported so the
// data modules can build concepts, but it is intentionally NOT re-exported by the
// facade — it stays library-internal (per the L-0 contract).
//
// PURE / Never-PHI: no Firebase, no Firestore, no PHI, no external APIs. Concept
// records stay pure JSON-serializable data (no RegExp); recognition patterns are a
// separate code-level table owned by the data modules / index.js.
// ============================================================================

import {
  EVIDENCE_DOCUMENT_TYPES,
  EVIDENCE_VALUE_TYPES,
  EVIDENCE_FIELD_MODES,
  RED_FLAG_EVIDENCE_MODE,
} from '../evidenceMap.js';

// ── Concept schema contract version (this document = 1) ──────────────────────
export const CONCEPT_SCHEMA_VERSION = 1;

// ── Concept type enum (semantic class — orthogonal to value type) ────────────
export const CONCEPT_TYPES = Object.freeze({
  MEASUREMENT:            'measurement',
  FINDING:                'finding',
  DIAGNOSIS:              'diagnosis',
  SYMPTOM:                'symptom',
  MEDICATION_EXPOSURE:    'medication_exposure',
  PROCEDURE:              'procedure',
  DOCUMENT:               'document',
  DATE_EVENT:             'date_event',
  RED_FLAG:               'red_flag',
  OPTION_VALUE:           'option_value',
  ATTACHMENT_REQUIREMENT: 'attachment_requirement',
});

const CONCEPT_TYPE_VALUES = Object.freeze(Object.values(CONCEPT_TYPES));

// ── Pack binding target types (the four live binding points in normalizePackDoc)
export const TARGET_TYPES = Object.freeze({
  FIELD:                  'FIELD',
  OPTION:                 'OPTION',
  RED_FLAG:               'RED_FLAG',
  ATTACHMENT_REQUIREMENT: 'ATTACHMENT_REQUIREMENT',
});

const TARGET_TYPE_VALUES = Object.freeze(Object.values(TARGET_TYPES));

// ── Value-type + evidence-mode vocabularies ──────────────────────────────────
// SUPERSETS of the authoring enums in evidenceMap.js. The library carries the
// extra meaning ("document_presence" / "document_type_or_title") and projects
// DOWN to the existing authoring enums at binding time (2C-2c). The published-
// pack validators in evidenceMap.js are intentionally NOT touched.
export const CONCEPT_VALUE_TYPES = Object.freeze([
  ...EVIDENCE_VALUE_TYPES,          // number, date, text, boolean, option
  'document_presence',
]);

export const CONCEPT_EVIDENCE_MODES = Object.freeze([
  ...EVIDENCE_FIELD_MODES,          // keyword, nearby_value, option_alias, evidence_only
  RED_FLAG_EVIDENCE_MODE,           // positive_only
  'document_type_or_title',
]);

// ── Governance enums (forward-looking; only SYSTEM_SEEDED exists in v1 static) ─
export const GOVERNANCE_SOURCES = Object.freeze({
  SYSTEM_SEEDED:       'SYSTEM_SEEDED',
  SPECIALIST_APPROVED: 'SPECIALIST_APPROVED',
  TENANT_APPROVED:     'TENANT_APPROVED',
  BEHAVIOR_PROPOSED:   'BEHAVIOR_PROPOSED',
  PLATFORM_APPROVED:   'PLATFORM_APPROVED',
});
const GOVERNANCE_SOURCE_VALUES = Object.freeze(Object.values(GOVERNANCE_SOURCES));

export const OWNER_SCOPES = Object.freeze({
  GLOBAL:    'GLOBAL',
  SPECIALTY: 'SPECIALTY',
  TENANT:    'TENANT',
});
const OWNER_SCOPE_VALUES = Object.freeze(Object.values(OWNER_SCOPES));

export const CONCEPT_STATUS = Object.freeze({
  ACTIVE:     'ACTIVE',
  DEPRECATED: 'DEPRECATED',
});
const CONCEPT_STATUS_VALUES = Object.freeze(Object.values(CONCEPT_STATUS));

export const CONCEPT_REVIEW_STATUS = Object.freeze({
  DRAFT:    'DRAFT',
  PROPOSED: 'PROPOSED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
});
const CONCEPT_REVIEW_STATUS_VALUES = Object.freeze(Object.values(CONCEPT_REVIEW_STATUS));

// Polarity precedence (locked): negative > uncertainty > positive.
export const DEFAULT_POLARITY_RULE = 'negative_overrides_positive';

// "shared"/global is represented as specialtyId = null. The string 'shared' is
// also accepted on input for resolution helpers (per contract §7).
export const SHARED_SPECIALTY_ID = null;

// Default matcher scoring rules (seed; the matcher in 2C-3 consumes these).
const DEFAULT_CONFIDENCE_RULES = Object.freeze({
  exactAlias:             0.95,
  nearbyValue:            0.9,
  phraseMatch:            0.8,
  weakPartial:            0.5,
  preferredDocumentBoost: 0.05,
  weakDocumentPenalty:   -0.1,
  negationPenalty:       -1,
});

// conceptType → default value model.
const CONCEPT_TYPE_VALUE_DEFAULTS = Object.freeze({
  measurement:            { valueType: 'number',            evidenceMode: 'nearby_value',          requiresNearbyValue: true  },
  finding:                { valueType: 'option',            evidenceMode: 'option_alias',          requiresNearbyValue: false },
  diagnosis:              { valueType: 'option',            evidenceMode: 'option_alias',          requiresNearbyValue: false },
  symptom:                { valueType: 'option',            evidenceMode: 'keyword',               requiresNearbyValue: false },
  medication_exposure:    { valueType: 'option',            evidenceMode: 'keyword',               requiresNearbyValue: false },
  procedure:              { valueType: 'option',            evidenceMode: 'keyword',               requiresNearbyValue: false },
  document:               { valueType: 'document_presence', evidenceMode: 'document_type_or_title', requiresNearbyValue: false },
  date_event:             { valueType: 'date',              evidenceMode: 'nearby_value',          requiresNearbyValue: true  },
  red_flag:               { valueType: 'boolean',           evidenceMode: 'positive_only',         requiresNearbyValue: false },
  option_value:           { valueType: 'option',            evidenceMode: 'option_alias',          requiresNearbyValue: false },
  attachment_requirement: { valueType: 'document_presence', evidenceMode: 'document_type_or_title', requiresNearbyValue: false },
});

// conceptType → default target compatibility (the four live Pack binding points).
const CONCEPT_TYPE_TARGET_DEFAULTS = Object.freeze({
  measurement:            { compatibleTargetTypes: ['FIELD'],                  compatibleFieldTypes: ['number'],                                       allowedForRedFlag: false, allowedForAttachment: false },
  finding:                { compatibleTargetTypes: ['OPTION'],                 compatibleFieldTypes: ['select', 'radio', 'multiselect', 'checkbox'],   allowedForRedFlag: false, allowedForAttachment: false },
  diagnosis:              { compatibleTargetTypes: ['OPTION'],                 compatibleFieldTypes: ['select', 'radio', 'multiselect', 'checkbox'],   allowedForRedFlag: false, allowedForAttachment: false },
  symptom:                { compatibleTargetTypes: ['OPTION', 'FIELD'],        compatibleFieldTypes: ['select', 'radio', 'multiselect', 'checkbox', 'text', 'textarea'], allowedForRedFlag: false, allowedForAttachment: false },
  medication_exposure:    { compatibleTargetTypes: ['OPTION'],                 compatibleFieldTypes: ['select', 'radio', 'multiselect', 'checkbox'],   allowedForRedFlag: false, allowedForAttachment: false },
  procedure:              { compatibleTargetTypes: ['OPTION'],                 compatibleFieldTypes: ['select', 'radio', 'multiselect', 'checkbox'],   allowedForRedFlag: false, allowedForAttachment: false },
  document:               { compatibleTargetTypes: ['ATTACHMENT_REQUIREMENT'], compatibleFieldTypes: [],                                               allowedForRedFlag: false, allowedForAttachment: true  },
  date_event:             { compatibleTargetTypes: ['FIELD'],                  compatibleFieldTypes: ['date'],                                         allowedForRedFlag: false, allowedForAttachment: false },
  red_flag:               { compatibleTargetTypes: ['RED_FLAG'],               compatibleFieldTypes: [],                                               allowedForRedFlag: true,  allowedForAttachment: false },
  option_value:           { compatibleTargetTypes: ['OPTION'],                 compatibleFieldTypes: ['select', 'radio', 'multiselect', 'checkbox'],   allowedForRedFlag: false, allowedForAttachment: false },
  attachment_requirement: { compatibleTargetTypes: ['ATTACHMENT_REQUIREMENT'], compatibleFieldTypes: [],                                               allowedForRedFlag: false, allowedForAttachment: true  },
});

// ── pure helpers ─────────────────────────────────────────────────────────────

const freezeDeep = (obj) => {
  if (obj && typeof obj === 'object' && !Object.isFrozen(obj)) {
    Object.values(obj).forEach(freezeDeep);
    Object.freeze(obj);
  }
  return obj;
};

/** Normalize a list of terms: trim, drop-empty, lowercase, case-insensitive dedupe. */
const deriveMatchTerms = (...lists) => {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      if (typeof raw !== 'string') continue;
      const term = raw.trim().toLowerCase();
      if (!term || seen.has(term)) continue;
      seen.add(term);
      out.push(term);
    }
  }
  return out;
};

const dedupe = (list) => {
  const seen = new Set();
  const out = [];
  for (const v of (Array.isArray(list) ? list : [])) {
    if (typeof v !== 'string') continue;
    const t = v.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
};

// ============================================================================
// CONCEPT BUILDER
// Assembles a fully-populated, frozen concept object. Every key is present and
// non-undefined; conceptType drives value + target defaults so records stay
// terse while remaining first-class and contract-complete.
// ============================================================================

export const makeConcept = (opts) => {
  const conceptType = opts.conceptType;
  const specialtyId = opts.specialtyId === undefined ? SHARED_SPECIALTY_ID : opts.specialtyId;
  const isRedFlag = conceptType === CONCEPT_TYPES.RED_FLAG;

  const valueDef = CONCEPT_TYPE_VALUE_DEFAULTS[conceptType] || { valueType: 'text', evidenceMode: 'keyword', requiresNearbyValue: false };
  const targetDef = CONCEPT_TYPE_TARGET_DEFAULTS[conceptType] || { compatibleTargetTypes: [], compatibleFieldTypes: [], allowedForRedFlag: false, allowedForAttachment: false };

  // ── value model ──
  const value = {
    valueType:           opts.valueType || valueDef.valueType,
    evidenceMode:        opts.evidenceMode || valueDef.evidenceMode,
    preferredUnit:       opts.preferredUnit != null ? opts.preferredUnit : null,
    allowedUnits:        dedupe(opts.allowedUnits),
    valuePattern:        opts.valuePattern != null ? opts.valuePattern : null,
    allowUnitConversion: false, // LOCKED false in v1 — surface evidence, never convert
    requiresNearbyValue: opts.requiresNearbyValue != null ? opts.requiresNearbyValue : valueDef.requiresNearbyValue,
    allowedValues:       dedupe(opts.allowedValues),
  };
  // Red-flag safety invariant carried on the value model.
  if (isRedFlag) value.requireExplicitPositiveEvidence = true;

  // ── language model ──
  const aliases = dedupe(opts.aliases);
  const abbreviations = dedupe(opts.abbreviations);
  const phrases = dedupe(opts.phrases);
  const positivePhrases = dedupe(opts.positivePhrases);
  const matchTerms = Array.isArray(opts.matchTerms) && opts.matchTerms.length
    ? deriveMatchTerms(opts.matchTerms)
    : deriveMatchTerms(aliases.length ? aliases : positivePhrases, phrases);

  const language = { aliases, abbreviations, phrases, matchTerms };

  // ── evidence model ──
  const preferredDocumentTypes = dedupe(opts.preferredDocumentTypes);
  const acceptableDocumentTypes = dedupe(opts.acceptableDocumentTypes);
  const weakDocumentTypes = dedupe(opts.weakDocumentTypes);
  const evidence = {
    preferredDocumentTypes,
    acceptableDocumentTypes,
    weakDocumentTypes,
    sourcePreference: Array.isArray(opts.sourcePreference) && opts.sourcePreference.length
      ? dedupe(opts.sourcePreference)
      : dedupe([...preferredDocumentTypes, ...acceptableDocumentTypes, ...weakDocumentTypes]),
    snippetWindowChars: opts.snippetWindowChars != null ? opts.snippetWindowChars : 120,
    pageNumberAllowed:  opts.pageNumberAllowed != null ? opts.pageNumberAllowed : true,
  };

  // ── matching model ──
  const matching = {
    matchMode:           opts.matchMode || 'whole_word_or_label',
    requiresNearbyValue: opts.requiresNearbyValue != null ? opts.requiresNearbyValue : valueDef.requiresNearbyValue,
    nearbyWindowChars:   opts.nearbyWindowChars != null ? opts.nearbyWindowChars : 80,
    confidenceRules:     { ...DEFAULT_CONFIDENCE_RULES, ...(opts.confidenceRules || {}) },
  };

  // ── safety / semantic boundary ──
  const safety = {
    positivePhrases,
    negativePhrases:    dedupe(opts.negativePhrases),
    uncertaintyPhrases: dedupe(opts.uncertaintyPhrases),
    excludedPhrases:    dedupe(opts.excludedPhrases),
    commonFalseMatches: dedupe(opts.commonFalseMatches),
    polarityRule:       opts.polarityRule || DEFAULT_POLARITY_RULE,
  };

  // ── target compatibility ──
  const target = {
    compatibleTargetTypes: Array.isArray(opts.compatibleTargetTypes) ? dedupe(opts.compatibleTargetTypes) : targetDef.compatibleTargetTypes.slice(),
    compatibleFieldTypes:  Array.isArray(opts.compatibleFieldTypes) ? dedupe(opts.compatibleFieldTypes) : targetDef.compatibleFieldTypes.slice(),
    allowedForRedFlag:     opts.allowedForRedFlag != null ? opts.allowedForRedFlag : targetDef.allowedForRedFlag,
    allowedForAttachment:  opts.allowedForAttachment != null ? opts.allowedForAttachment : targetDef.allowedForAttachment,
  };

  // ── governance ──
  const governance = {
    source:         opts.source || GOVERNANCE_SOURCES.SYSTEM_SEEDED,
    ownerScope:     opts.ownerScope || (specialtyId == null ? OWNER_SCOPES.GLOBAL : OWNER_SCOPES.SPECIALTY),
    status:         opts.status || CONCEPT_STATUS.ACTIVE,
    reviewStatus:   opts.reviewStatus || CONCEPT_REVIEW_STATUS.APPROVED,
    version:        opts.version != null ? opts.version : 1,
    approvedByRole: opts.approvedByRole != null ? opts.approvedByRole : null,
    createdAt:      null, // null in static v1; set when Firestore-managed
    updatedAt:      null,
  };

  return freezeDeep({
    conceptId:      opts.conceptId,
    schemaVersion:  CONCEPT_SCHEMA_VERSION,
    specialtyId,
    conceptType,
    canonicalLabel: opts.canonicalLabel,
    description:    opts.description != null ? opts.description : '',
    value,
    language,
    evidence,
    matching,
    safety,
    target,
    governance,
  });
};

// ============================================================================
// TARGET RESOLUTION
// ============================================================================

/**
 * Resolve a concept's target compatibility, falling back to the conceptType
 * defaults when a concept omits explicit target arrays.
 * @param {Object} concept
 * @returns {{ compatibleTargetTypes: string[], compatibleFieldTypes: string[], allowedForRedFlag: boolean, allowedForAttachment: boolean }}
 */
export const resolveTargetCompatibility = (concept) => {
  const def = CONCEPT_TYPE_TARGET_DEFAULTS[concept?.conceptType] ||
    { compatibleTargetTypes: [], compatibleFieldTypes: [], allowedForRedFlag: false, allowedForAttachment: false };
  const t = concept?.target || {};
  return {
    compatibleTargetTypes: Array.isArray(t.compatibleTargetTypes) && t.compatibleTargetTypes.length
      ? t.compatibleTargetTypes.slice() : def.compatibleTargetTypes.slice(),
    compatibleFieldTypes: Array.isArray(t.compatibleFieldTypes) && t.compatibleFieldTypes.length
      ? t.compatibleFieldTypes.slice() : def.compatibleFieldTypes.slice(),
    allowedForRedFlag: typeof t.allowedForRedFlag === 'boolean' ? t.allowedForRedFlag : def.allowedForRedFlag,
    allowedForAttachment: typeof t.allowedForAttachment === 'boolean' ? t.allowedForAttachment : def.allowedForAttachment,
  };
};

// ============================================================================
// VALIDATOR (pure — returns string[] of human-readable errors; [] = valid)
// ============================================================================

const SNAKE_CASE = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;
const isArr = (v) => Array.isArray(v);
const isStr = (v) => typeof v === 'string' && v.trim().length > 0;

/** Collect dotted paths of any `undefined` value anywhere in a structure. */
const findUndefinedPaths = (obj, base = '') => {
  const out = [];
  if (obj === undefined) { out.push(base || '(root)'); return out; }
  if (obj === null || typeof obj !== 'object') return out;
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => out.push(...findUndefinedPaths(v, `${base}[${i}]`)));
  } else {
    for (const [k, v] of Object.entries(obj)) {
      out.push(...findUndefinedPaths(v, base ? `${base}.${k}` : k));
    }
  }
  return out;
};

/**
 * Validate a single concept against the foundation contract.
 * @param {Object} concept
 * @returns {string[]} errors (empty = valid)
 */
export const validateEvidenceConcept = (concept) => {
  const errors = [];
  if (!concept || typeof concept !== 'object') {
    errors.push('Concept must be an object.');
    return errors;
  }
  const id = isStr(concept.conceptId) ? concept.conceptId : '(missing conceptId)';
  const E = (msg) => errors.push(`[${id}] ${msg}`);

  // identity
  if (!isStr(concept.conceptId)) E('conceptId is required.');
  else if (!SNAKE_CASE.test(concept.conceptId)) E('conceptId must be stable snake_case.');
  if (typeof concept.schemaVersion !== 'number') E('schemaVersion must be a number.');
  if (!CONCEPT_TYPE_VALUES.includes(concept.conceptType)) E(`conceptType "${concept.conceptType}" is invalid.`);
  if (!isStr(concept.canonicalLabel)) E('canonicalLabel is required.');
  if (!(concept.specialtyId === null || typeof concept.specialtyId === 'string')) E('specialtyId must be a string or null.');

  // value
  const value = concept.value;
  if (!value || typeof value !== 'object') {
    E('value model is required.');
  } else {
    if (!isStr(value.valueType)) E('value.valueType is required.');
    else if (!CONCEPT_VALUE_TYPES.includes(value.valueType)) E(`value.valueType "${value.valueType}" is invalid.`);
    if (!isStr(value.evidenceMode)) E('value.evidenceMode is required.');
    else if (!CONCEPT_EVIDENCE_MODES.includes(value.evidenceMode)) E(`value.evidenceMode "${value.evidenceMode}" is invalid.`);
    if (value.allowUnitConversion !== false) E('value.allowUnitConversion must be false (locked in v1).');
    if (!isArr(value.allowedUnits)) E('value.allowedUnits must be an array.');
    if (!isArr(value.allowedValues)) E('value.allowedValues must be an array.');
  }

  // language
  const language = concept.language;
  if (!language || typeof language !== 'object') {
    E('language model is required.');
  } else {
    if (!isArr(language.aliases)) E('language.aliases must be an array.');
    if (!isArr(language.abbreviations)) E('language.abbreviations must be an array.');
    if (!isArr(language.phrases)) E('language.phrases must be an array.');
    if (!isArr(language.matchTerms)) E('language.matchTerms must be an array.');
  }

  // evidence document types must be canonical
  const evidence = concept.evidence;
  if (!evidence || typeof evidence !== 'object') {
    E('evidence model is required.');
  } else {
    for (const key of ['preferredDocumentTypes', 'acceptableDocumentTypes', 'weakDocumentTypes', 'sourcePreference']) {
      if (!isArr(evidence[key])) { E(`evidence.${key} must be an array.`); continue; }
      for (const dt of evidence[key]) {
        if (!EVIDENCE_DOCUMENT_TYPES.includes(dt)) E(`evidence.${key} has invalid document type "${dt}".`);
      }
    }
  }

  // matching
  const matching = concept.matching;
  if (!matching || typeof matching !== 'object') E('matching model is required.');
  else if (!matching.confidenceRules || typeof matching.confidenceRules !== 'object') E('matching.confidenceRules is required.');

  // safety
  const safety = concept.safety;
  if (!safety || typeof safety !== 'object') {
    E('safety model is required.');
  } else {
    for (const key of ['positivePhrases', 'negativePhrases', 'uncertaintyPhrases', 'excludedPhrases', 'commonFalseMatches']) {
      if (!isArr(safety[key])) E(`safety.${key} must be an array.`);
    }
  }

  // target compatibility
  const target = concept.target;
  if (!target || typeof target !== 'object') {
    E('target model is required.');
  } else {
    if (!isArr(target.compatibleTargetTypes) || target.compatibleTargetTypes.length === 0) {
      E('target.compatibleTargetTypes must be a non-empty array.');
    } else {
      for (const tt of target.compatibleTargetTypes) {
        if (!TARGET_TYPE_VALUES.includes(tt)) E(`target.compatibleTargetTypes has invalid value "${tt}".`);
      }
    }
    if (!isArr(target.compatibleFieldTypes)) E('target.compatibleFieldTypes must be an array.');
    if (typeof target.allowedForRedFlag !== 'boolean') E('target.allowedForRedFlag must be a boolean.');
    if (typeof target.allowedForAttachment !== 'boolean') E('target.allowedForAttachment must be a boolean.');
  }

  // governance
  const g = concept.governance;
  if (!g || typeof g !== 'object') {
    E('governance model is required.');
  } else {
    if (!GOVERNANCE_SOURCE_VALUES.includes(g.source)) E(`governance.source "${g.source}" is invalid.`);
    if (!OWNER_SCOPE_VALUES.includes(g.ownerScope)) E(`governance.ownerScope "${g.ownerScope}" is invalid.`);
    if (!CONCEPT_STATUS_VALUES.includes(g.status)) E(`governance.status "${g.status}" must be ACTIVE or DEPRECATED.`);
    if (!CONCEPT_REVIEW_STATUS_VALUES.includes(g.reviewStatus)) E(`governance.reviewStatus "${g.reviewStatus}" is invalid.`);
  }

  // red-flag safety invariants
  if (concept.conceptType === CONCEPT_TYPES.RED_FLAG) {
    if (value && value.evidenceMode !== RED_FLAG_EVIDENCE_MODE) E(`red_flag value.evidenceMode must be "${RED_FLAG_EVIDENCE_MODE}".`);
    if (value && value.requireExplicitPositiveEvidence !== true) E('red_flag value.requireExplicitPositiveEvidence must be true.');
    if (safety && (!isArr(safety.positivePhrases) || safety.positivePhrases.length === 0)) E('red_flag safety.positivePhrases must be non-empty.');
    if (safety && !isArr(safety.negativePhrases)) E('red_flag safety.negativePhrases must exist (array).');
    if (target && !(isArr(target.compatibleTargetTypes) && target.compatibleTargetTypes.includes(TARGET_TYPES.RED_FLAG))) {
      E('red_flag target.compatibleTargetTypes must include RED_FLAG.');
    }
    if (target && target.allowedForRedFlag !== true) E('red_flag target.allowedForRedFlag must be true.');
  }

  // no undefined anywhere
  const undef = findUndefinedPaths(concept);
  if (undef.length) E(`contains undefined value(s) at: ${undef.join(', ')}.`);

  return errors;
};

// ============================================================================
// PROJECTIONS — concept → 2C-1 authoring-shape (pure; never the full concept)
// Consumed by evidenceMapDrafting.js (Phase 2C-2b). Behavior-preserving.
// ============================================================================

/**
 * Project a concept into the 2C-1 FIELD evidence-map shape. valueType/evidenceMode
 * are supplied by the FIELD TYPE (not the concept) to preserve drafter behavior;
 * units come from the concept only when includeUnits is true. Returns null for a
 * null concept (caller owns the slug fallback).
 * @param {Object|null} concept
 * @param {{ valueType?: string, evidenceMode?: string, includeUnits?: boolean }} opts
 * @returns {Object|null}
 */
export const conceptToFieldEvidenceMap = (concept, opts = {}) => {
  if (!concept) return null;
  const { valueType = '', evidenceMode = '', includeUnits = false } = opts;
  return {
    conceptId:      concept.conceptId,
    canonicalLabel: concept.canonicalLabel,
    aliases:        dedupe(concept.language.aliases),
    phrases:        [],
    valueType:      valueType || '',
    units:          includeUnits ? dedupe(concept.value.allowedUnits) : [],
    evidenceMode:   evidenceMode || '',
    negativePhrases:    [],
    uncertaintyPhrases: [],
  };
};

/**
 * Project a (possibly null) finding concept + option label into the 2C-1 OPTION
 * evidence shape. Aliases come from the concept (or the option label); the
 * positive/negative/uncertainty phrases are generated from the option label —
 * exactly as the former draftOptionEvidence did.
 * @param {Object|null} concept
 * @param {string} optionLabel
 * @returns {{ aliases: string[], phrases: string[], negativePhrases: string[], uncertaintyPhrases: string[] }}
 */
export const conceptToOptionEvidenceMap = (concept, optionLabel) => {
  const term = String(optionLabel || '').trim();
  if (!term) return { aliases: [], phrases: [], negativePhrases: [], uncertaintyPhrases: [] };
  const aliases = concept ? dedupe(concept.language.aliases) : dedupe([term]);
  const lc = term.toLowerCase();
  return {
    aliases,
    phrases:            dedupe([`possible ${lc}`]),
    negativePhrases:    dedupe([`no ${lc}`, `${lc} not present`]),
    uncertaintyPhrases: dedupe([`rule out ${lc}`, `possible ${lc}`]),
  };
};

/**
 * Project a red-flag concept into the 2C-1 RED FLAG evidence shape (positive_only,
 * always requireExplicitPositiveEvidence). Returns null for a null concept (caller
 * owns the label-derived fallback).
 * @param {Object|null} concept
 * @returns {Object|null}
 */
export const conceptToRedFlagEvidenceMap = (concept) => {
  if (!concept) return null;
  return {
    positivePhrases:    dedupe(concept.safety.positivePhrases),
    negativePhrases:    dedupe(concept.safety.negativePhrases),
    uncertaintyPhrases: dedupe(concept.safety.uncertaintyPhrases),
    evidenceMode:       RED_FLAG_EVIDENCE_MODE,   // always positive_only
    requireExplicitPositiveEvidence: true,        // red flags never auto-select
  };
};

/**
 * Project a (possibly null) concept + attachment context into the 2C-1 ATTACHMENT
 * evidence shape. documentTypeHints are computed by the caller (attachmentType +
 * inferDocumentTypeFromText) since they depend on attachment context, not the
 * matched concept — exactly as the former draftAttachmentEvidenceMap did.
 * @param {Object|null} concept
 * @param {{ label?: string, documentTypeHints?: string[] }} ctx
 * @returns {{ documentTypeHints: string[], aliases: string[], phrases: string[] }}
 */
export const conceptToAttachmentEvidenceMap = (concept, ctx = {}) => {
  const label = String(ctx.label || '').trim();
  const aliases = concept ? dedupe(concept.language.aliases) : dedupe([label]);
  const phrases = dedupe([label, ...aliases.map((a) => `${a} result`)]);
  return {
    documentTypeHints: dedupe(ctx.documentTypeHints),
    aliases,
    phrases,
  };
};
